import React, { useState } from "react";
import { Box } from "@mui/material";

import { StyledWhiteContainer } from "features/metadesk/settings/common.styles";
import XLinkDropDown from "./XLinkDropDown";
import CustomToggle from "components/CustomToggle/CustomToggle";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import Percent from "components/Percent/Percent";
import { formatDate, formatTime } from "features/helpers/utils";
import { marketingColors } from "features/configure/chartColors";
import { useIntl } from "react-intl";
import useGetXChart from "./hooks/useGetXChart";
import useNavigateEntity from "hooks/useNavigateEntity";
import { StyledSkeleton } from "components/LoadingComponent/LoadingComponent.styles";
import DualYAxisChartContainer from "components/DualYAxisChart/DualYAxisChartContainer";

export default function XPerformanceChart({ postLinks }) {
  const [selectedYAxis, setSelectedYAxis] = useState(["impressions", "likes"]);
  const [totalSelectedLinkAmount, setTotalSelectedLinkAmount] = useState(0);
  const { entityName } = useNavigateEntity();
  const { formatMessage } = useIntl();

  const totals = [
    {
      label: formatMessage({
        id: "xCampaignTracking.statistic.totalImpression",
      }),
      key: "impressions",
      name: formatMessage({
        id: "global.filter.impression",
      }),
      tooltip: formatMessage({
        id: "xCampaignTracking.totalImpression.tooltip",
      }),
    },
    {
      label: formatMessage({
        id: "xCampaignTracking.statistic.totalLikes",
      }),
      key: "likes",
      name: formatMessage({
        id: "global.filter.like",
      }),
      tooltip: formatMessage({ id: "xCampaignTracking.totalLikes.tooltip" }),
    },
    {
      label: formatMessage({
        id: "xCampaignTracking.statistic.totalComments",
      }),
      key: "comments",
      name: formatMessage({
        id: "global.filter.comment",
      }),
      tooltip: formatMessage({ id: "xCampaignTracking.totalComments.tooltip" }),
    },
    {
      label: formatMessage({
        id: "xCampaignTracking.statistic.totalRetweets",
      }),
      key: "reposts",
      name: formatMessage({
        id: "global.filter.retweet",
      }),
      tooltip: formatMessage({ id: "xCampaignTracking.totalRetweets.tooltip" }),
    },
    {
      label: formatMessage({
        id: "xCampaignTracking.statistic.totalBookmarks",
      }),
      key: "bookmarks",
      name: formatMessage({
        id: "global.filter.bookmark",
      }),
      tooltip: formatMessage({
        id: "xCampaignTracking.totalBookmarks.tooltip",
      }),
    },
  ];

  const colors = marketingColors.small;
  const { data, link, day, setLink, setDay, isLoading } = useGetXChart();

  const {
    chartData: lineChartData,
    diffData: barChartData,
    total: totalData,
  } = data;

  const xAxisData = lineChartData.map((d) => {
    if (day === "1")
      return (
        formatDate(d.date, { month: true, utc0: true }) +
        "\n" +
        formatTime(d.date, { utc0: true })
      );
    return formatDate(d.date, { month: true });
  }); // 去重後的日期

  const dualYAxisChartData = {
    xAxisData,
    yAxisData: {
      lineChartData,
      barChartData,
    },
  };

  const chartInfos = [
    {
      title: "xCampaignTracking.chart.dailyTrends.title",
      tooltip: "xCampaignTracking.chart.dailyTrends.tooltip",
      showYAxisDropdown: true,
      type: "line",
      hasData: lineChartData?.length > 0,
    },
    {
      title: "xCampaignTracking.chart.dailyChanges.title",
      tooltip: "xCampaignTracking.chart.dailyChanges.tooltip",
      showYAxisDropdown: false,
      type: "bar",
      hasData: barChartData?.length > 0,
    },
  ];

  const daysOptions =
    entityName === "greyball"
      ? [
          { text: formatMessage({ id: "datePicker.range.day" }), value: "1" },
          { text: formatMessage({ id: "datePicker.range.week" }), value: "7" },
          {
            text: formatMessage({ id: "datePicker.range.month" }),
            value: "30",
          },
        ]
      : [
          { text: formatMessage({ id: "datePicker.range.day" }), value: "1" },
          { text: formatMessage({ id: "datePicker.range.week" }), value: "7" },
          // { text: "All", value: "" },
        ];

  const renderTotalStats = (total, index) => (
    <Box className="item-flex" my="2px">
      <Box
        height="52px"
        sx={{
          minWidth: "113px",
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
        }}
      >
        <Box className="item-flex" gap="4px">
          <Box fontSize={16} color="#777">
            {total.label}
          </Box>
          <IconTooltip content={total.tooltip} customColor="#A5A5A5" />
        </Box>
        <Box className="item-flex" justifyContent="space-between" gap="10px">
          {isLoading ? (
            <StyledSkeleton
              animation="wave"
              sx={{ width: "80%", height: "24px", margin: "2px 0" }}
            />
          ) : (
            <>
              <Box fontSize={24} fontWeight={700} color="#555">
                {totalData[total.key]?.count.toLocaleString()}
              </Box>
              {day && (
                <Percent
                  data={
                    totalData[total.key]?.growth === "-"
                      ? "-"
                      : (totalData[total.key]?.growth * 100).toFixed(2)
                  }
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <StyledWhiteContainer sx={{ marginBottom: "8px", padding: "8px 5px" }}>
        <Box
          className="item-flex"
          sx={{ justifyContent: "flex-end", gap: "8px" }}
        >
          <Box className="item-flex" sx={{ gap: "8px", flexWrap: "wrap" }}>
            <XLinkDropDown
              link={link}
              setLink={setLink}
              options={postLinks}
              totalSelectedLinkAmount={totalSelectedLinkAmount}
              setTotalSelectedLinkAmount={setTotalSelectedLinkAmount}
            />
          </Box>
          <CustomToggle
            data={daysOptions}
            value={day}
            onChange={(e, newValue) => {
              if (newValue == null) return;
              setDay(newValue);
            }}
          />
        </Box>
      </StyledWhiteContainer>
      <StyledWhiteContainer sx={{ marginBottom: "8px", paddingBottom: 0 }}>
        <Box
          className="item-flex"
          justifyContent="space-around"
          flexWrap="wrap"
          width="100%"
        >
          {totals.map((total, index) => (
            <>
              {renderTotalStats(total, index)}
              {index < totals.length - 1 && (
                <Box width="1px" height="52px" bgcolor="#E6E6E6" mx="12px" />
              )}
            </>
          ))}
        </Box>
        <DualYAxisChartContainer
          data={dualYAxisChartData}
          yAxisInfos={totals}
          selectedYAxis={selectedYAxis}
          setSelectedYAxis={setSelectedYAxis}
          colors={colors.slice(1, -1)}
          chartInfos={chartInfos}
          isLoading={isLoading}
          customStyle={{ containerStyle: { paddingTop: "24px" } }}
        />
      </StyledWhiteContainer>
    </>
  );
}
